import { create, fromBinary, fromJson, JsonValue, Message, toBinary, toJson } from '@bufbuild/protobuf';
import { GenMessage } from '@bufbuild/protobuf/codegenv2';
import { AxiosRequestConfig } from 'axios';
import { isDebugModeEnabled } from 'features/debug/utils/debug-mode';
import { apiFactory } from 'hooks/api/httpApi';
import { isNullOrUndefined } from 'utils/utils';
import FROM_JSON_CONFIG from './constant/from-json-config';

export class ProtobufClient {
    private static LOCAL_ENVIRONMENT = import.meta.env.VITE_APP_ENV !== 'production' || isDebugModeEnabled();

    constructor(private api: ReturnType<typeof apiFactory>) {}

    private static modifyRequestConfig(config: AxiosRequestConfig = {}, protobuf: boolean): AxiosRequestConfig {
        return {
            ...config,
            responseType: protobuf ? 'arraybuffer' : 'json',
            headers: {
                'Content-Type': protobuf ? 'application/x-protobuf' : 'application/json',
                Accept: protobuf ? 'application/x-protobuf' : 'application/json',
            },
        };
    }

    private static toBinary<T extends Message>(schema: GenMessage<T>, params: object): Uint8Array {
        if (isNullOrUndefined(schema)) return null;
        return toBinary(schema, create(schema, params));
    }

    private static toJson<T extends Message>(schema: GenMessage<T>, params: object): JsonValue {
        if (isNullOrUndefined(schema)) return null;
        return toJson(schema, create(schema, params));
    }

    private static handleJsonResponse<T extends Message>(schema: GenMessage<T>, data: JsonValue): T {
        if (isNullOrUndefined(schema) || typeof data === 'string') return null;
        return fromJson(schema, data, FROM_JSON_CONFIG);
    }

    private static handleBinaryResponse<T extends Message>(schema: GenMessage<T>, data: Iterable<number>): T {
        if (isNullOrUndefined(schema)) return null;
        return fromBinary(schema, new Uint8Array(data));
    }

    public getRequest<T extends Message>(
        responseSchema: GenMessage<T>,
        path: string,
        params?: object,
        config?: AxiosRequestConfig
    ): Promise<T> {
        if (ProtobufClient.LOCAL_ENVIRONMENT) {
            return this.api
                .get<JsonValue>(path, params, ProtobufClient.modifyRequestConfig(config, false))
                .then((response) => ProtobufClient.handleJsonResponse(responseSchema, response.getData()));
        }

        return this.api
            .get<Iterable<number>>(path, params, ProtobufClient.modifyRequestConfig(config, true))
            .then((response) => ProtobufClient.handleBinaryResponse(responseSchema, response.getData()));
    }

    public postRequest<R extends Message, T extends Message>(
        requestSchema: GenMessage<R>,
        responseSchema: GenMessage<T>,
        path: string,
        params?: object,
        config?: AxiosRequestConfig
    ): Promise<T> {
        if (ProtobufClient.LOCAL_ENVIRONMENT) {
            return this.api
                .post<JsonValue>(
                    path,
                    ProtobufClient.toJson(requestSchema, params) as object,
                    ProtobufClient.modifyRequestConfig(config, false)
                )
                .then((response) => ProtobufClient.handleJsonResponse(responseSchema, response.getData()));
        }

        return this.api
            .post<
                Iterable<number>
            >(path, ProtobufClient.toBinary(requestSchema, params), ProtobufClient.modifyRequestConfig(config, true))
            .then((response) => ProtobufClient.handleBinaryResponse(responseSchema, response.getData()));
    }

    public patchRequest<R extends Message, T extends Message>(
        requestSchema: GenMessage<R>,
        responseSchema: GenMessage<T>,
        path: string,
        params?: object,
        config?: AxiosRequestConfig
    ): Promise<T> {
        if (ProtobufClient.LOCAL_ENVIRONMENT) {
            return this.api
                .patch<JsonValue>(
                    path,
                    ProtobufClient.toJson(requestSchema, params) as object,
                    ProtobufClient.modifyRequestConfig(config, false)
                )
                .then((response) => ProtobufClient.handleJsonResponse(responseSchema, response.getData()));
        }

        return this.api
            .patch<
                Iterable<number>
            >(path, ProtobufClient.toBinary(requestSchema, params), ProtobufClient.modifyRequestConfig(config, true))
            .then((response) => ProtobufClient.handleBinaryResponse(responseSchema, response.getData()));
    }

    public deleteRequest<T extends Message>(
        responseSchema: GenMessage<T>,
        path: string,
        config?: AxiosRequestConfig
    ): Promise<T> {
        if (ProtobufClient.LOCAL_ENVIRONMENT) {
            return this.api
                .deleteMethod<JsonValue>(path, ProtobufClient.modifyRequestConfig(config, false))
                .then((response) => ProtobufClient.handleJsonResponse(responseSchema, response.getData()));
        }

        return this.api
            .deleteMethod<Iterable<number>>(path, ProtobufClient.modifyRequestConfig(config, true))
            .then((response) => ProtobufClient.handleBinaryResponse(responseSchema, response.getData()));
    }

    public putRequest<R extends Message, T extends Message>(
        requestSchema: GenMessage<R>,
        responseSchema: GenMessage<T>,
        path: string,
        params?: object,
        config?: AxiosRequestConfig
    ): Promise<T> {
        if (ProtobufClient.LOCAL_ENVIRONMENT) {
            return this.api
                .put<JsonValue>(
                    path,
                    ProtobufClient.toJson(requestSchema, params) as object,
                    ProtobufClient.modifyRequestConfig(config, false)
                )
                .then((response) => ProtobufClient.handleJsonResponse(responseSchema, response.getData()));
        }

        return this.api
            .put<
                Iterable<number>
            >(path, ProtobufClient.toBinary(requestSchema, params), ProtobufClient.modifyRequestConfig(config, true))
            .then((response) => ProtobufClient.handleBinaryResponse(responseSchema, response.getData()));
    }
}
